import { createRouter, createWebHistory, RouterView } from 'vue-router'
import { useAuthenticationStore } from '@/stores/AuthenticationStore'
import { useGlobalStore } from '@/stores/GlobalStore'
import { useLanguageStore } from '@/stores/LanguageStore'
import { storeToRefs } from 'pinia'
import Routes from '@/routes/routes'
import { useTrackingAction } from '@/vendors/tracking'
import { useSentry } from '@/vendors/integrations/sentry'

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes: [
    {
      path: '/:locale',
      component: RouterView,
      children: Routes,
      beforeEnter: to => {
        const { path: routePath, params, query } = to
        const { locale } = storeToRefs(useLanguageStore())
        const path = routePath === '/' ? `${routePath + locale.value}/` : routePath.replace(params.locale, locale.value)

        return params.locale !== locale.value ? { path, query } : true
      }
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: () => {
        const { locale } = storeToRefs(useLanguageStore())
        return { name: 'Home', params: { locale: locale.value } }
      }
    }
  ],
  scrollBehavior (to, from) {
    if (to.path === from.path && to.query !== from.query) {
      return { left: window.scrollX, top: window.scrollY }
    } else {
      return { left: 0, top: 0 }
    }
  }
})

router.beforeEach(async (to, from) => {
  const { logout, initAuthentication } = useAuthenticationStore()
  const { locale } = storeToRefs(useLanguageStore())
  const { trackPageViewed } = useTrackingAction()
  const sentry = useSentry()

  trackPageViewed({ to, from })

  try {
    const user = await initAuthentication()

    if (to.matched.some(route => route.meta.requiresAuth) && !user) {
      return { name: 'Login', params: { locale: locale.value }, query: { redirectTo: to.path } }
    } else if (to.matched.some(route => route.meta.requiresGuest) && user) {
      return to.query.redirectTo || { name: 'Home', params: { locale: locale.value } }
    }
  } catch (error) {
    if (error.status === 401) {
      logout()

      if (to.matched.some(route => route.meta.requiresAuth)) return { name: 'Login', params: { locale: locale.value }, query: { redirectTo: to.path } }
    } else {
      sentry.error(Error('#001601 - Navigation failed'))

      return { name: 'Home', params: { locale: locale.value } }
    }
  }
})

router.onError(error => {
  const { is_router_error } = storeToRefs(useGlobalStore())

  if (/ChunkLoadError:.*failed./i.test(error.message) || /Loading.*chunk.*failed./i.test(error.message)) {
    is_router_error.value = true
  }
})

export default router
